/**
 * DealChangePhaseDialog - Modal "Alterar Fase" do Negócio
 *
 * Espelho visual/funcional do `TaskChangePhaseDialog` (Tarefas), porém para
 * Negócios do CRM. Permite mover o negócio para outra fase — inclusive de
 * OUTRO pipeline e de OUTRA workspace do mesmo tenant — mesmo quando o
 * negócio já está GANHO ou PERDIDO (cenário de pós-venda/onboarding).
 *
 * Regras de segurança (autoridade final é do servidor, na RPC
 * `move_deal_to_phase` v2.7.0):
 *  - valida `can_change_phase` na fase de origem E na fase de destino;
 *  - bloqueia ganho → fase de perda e perdido → fase de ganho (exige reabrir);
 *  - preserva status/`actual_close_date` quando o negócio já está fechado.
 * Aqui no frontend apenas ocultamos/desabilitamos as opções inválidas para
 * evitar erro desnecessário ao usuário.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-12 v1.0.0 - Criação inicial (padronizado com Tarefas).
 */
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantWorkspacesPipelines } from '@/hooks/useTenantWorkspacesPipelines';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo (padrão de logging condicional do projeto)
const logger = createLogger('DealChangePhaseDialog');

/** Limite de caracteres da observação (proteção contra payload abusivo) */
const MAX_NOTES = 1000;

interface DealChangePhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Negócio alvo */
  dealId: string;
  /** Status atual do negócio (define quais fases finais ficam indisponíveis) */
  dealStatus: 'open' | 'won' | 'lost' | string;
  /** Workspace atual (pré-seleção) */
  currentWorkspaceId?: string | null;
  /** Pipeline atual (pré-seleção) */
  currentPipelineId?: string | null;
  /** Fase atual (exibição + validação) */
  currentPhaseId?: string | null;
  currentPhaseName?: string | null;
  currentPhaseColor?: string | null;
  /** Callback após sucesso (invalidações de cache ficam no pai) */
  onSuccess?: () => void;
}

export function DealChangePhaseDialog({
  open,
  onOpenChange,
  dealId,
  dealStatus,
  currentWorkspaceId,
  currentPipelineId,
  currentPhaseId,
  currentPhaseName,
  currentPhaseColor,
  onSuccess,
}: DealChangePhaseDialogProps) {
  const { currentTenant } = useAuth();

  // Estado local do formulário
  const [workspaceId, setWorkspaceId] = useState<string | undefined>(undefined);
  const [pipelineId, setPipelineId] = useState<string | undefined>(undefined);
  const [phaseId, setPhaseId] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Workspaces + pipelines + fases do tenant (lazy: só quando o modal abre)
  const { data: workspaces = [], isLoading } = useTenantWorkspacesPipelines({
    enabled: open,
  });

  // Reset ao abrir: pré-seleciona workspace/pipeline atuais
  useEffect(() => {
    if (open) {
      setWorkspaceId(currentWorkspaceId ?? undefined);
      setPipelineId(currentPipelineId ?? undefined);
      setPhaseId(undefined);
      setNotes('');
    }
  }, [open, currentWorkspaceId, currentPipelineId]);

  // Pipelines da workspace escolhida
  const pipelines = useMemo(
    () => workspaces.find((w) => w.id === workspaceId)?.pipelines ?? [],
    [workspaces, workspaceId],
  );

  // Negócio fechado não pode ir direto para fase final oposta (regra do servidor)
  const isClosed = dealStatus === 'won' || dealStatus === 'lost';

  // Fases disponíveis para destino (remove a fase atual e as fases inválidas)
  const availablePhases = useMemo(() => {
    const phases = pipelines.find((p) => p.id === pipelineId)?.phases ?? [];
    return phases.filter((f) => {
      if (f.id === currentPhaseId) return false;
      if (dealStatus === 'won' && f.is_lost) return false;
      if (dealStatus === 'lost' && f.is_won) return false;
      return true;
    });
  }, [pipelines, pipelineId, currentPhaseId, dealStatus]);

  const canSubmit = !!phaseId && phaseId !== currentPhaseId && !isSubmitting;

  /** Executa a mudança de fase (RPC) e, se houver, registra a observação */
  const handleSubmit = async () => {
    if (!phaseId) return;
    setIsSubmitting(true);
    try {
      const trimmed = notes.trim().slice(0, MAX_NOTES);

      // PASSO 1: move o negócio (a RPC valida permissões e regras de negócio)
      const { error } = await supabase.rpc('move_deal_to_phase', {
        p_deal_id: dealId,
        p_new_phase_id: phaseId,
      } as never);
      if (error) throw error;

      // PASSO 2: observação opcional vira um andamento (atividade) do negócio.
      // Falha aqui NÃO desfaz a mudança de fase — apenas avisa o usuário.
      if (trimmed) {
        const { error: noteError } = await supabase.rpc('create_deal_activity', {
          p_deal_id: dealId,
          p_activity_type: 'note',
          p_title: 'Observação da mudança de fase',
          p_description: trimmed,
          p_source: 'manual',
          p_tenant_hint: currentTenant?.id ?? null,
        } as never);
        if (noteError) {
          logger.error('Erro ao registrar observação da mudança de fase', noteError.message);
          toast.warning('Fase alterada, mas a observação não pôde ser registrada.');
        }
      }

      toast.success('Fase alterada com sucesso');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('Erro ao alterar fase do negócio', message);
      toast.error(`Erro ao alterar fase: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Cabeçalho padrão do sistema (mesmo do modal de Tarefas) */}
        <PremiumDialogHeader
          icon={GitBranch}
          title="Alterar Fase"
          description="Mova o negócio para outra fase, pipeline ou workspace."
        />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Fase atual */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Fase atual:</Label>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: currentPhaseColor || 'hsl(var(--muted-foreground))' }}
              />
              <span className="font-medium">{currentPhaseName || 'Sem fase'}</span>
            </div>
          </div>

          {/* Workspace */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Workspace</Label>
            <Select
              value={workspaceId}
              onValueChange={(v) => {
                setWorkspaceId(v);
                setPipelineId(undefined); // pipeline e fase precisam ser reescolhidos
                setPhaseId(undefined);
              }}
              disabled={isLoading || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue
                  placeholder={isLoading ? 'Carregando...' : 'Selecione a workspace'}
                />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((ws) => (
                  <SelectItem key={ws.id} value={ws.id}>
                    {ws.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pipeline */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Pipeline</Label>
            <Select
              value={pipelineId}
              onValueChange={(v) => {
                setPipelineId(v);
                setPhaseId(undefined); // fase precisa ser reescolhida
              }}
              disabled={!workspaceId || isLoading || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Selecione o pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((pl) => (
                  <SelectItem key={pl.id} value={pl.id}>
                    {pl.name}
                  </SelectItem>
                ))}
                {pipelines.length === 0 && !isLoading && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    Nenhum pipeline nesta workspace
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Nova fase */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Nova Fase</Label>
            <Select
              value={phaseId}
              onValueChange={setPhaseId}
              disabled={!pipelineId || isLoading || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Selecione a nova fase" />
              </SelectTrigger>
              <SelectContent>
                {availablePhases.map((phase) => (
                  <SelectItem key={phase.id} value={phase.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: phase.color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{phase.name}</span>
                    </span>
                  </SelectItem>
                ))}
                {availablePhases.length === 0 && !isLoading && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    Nenhuma outra fase disponível neste pipeline
                  </div>
                )}
              </SelectContent>
            </Select>
            {isClosed && (
              <p className="text-[11px] text-muted-foreground">
                O negócio continua marcado como{' '}
                {dealStatus === 'won' ? 'ganho' : 'perdido'} após a mudança. Para usar
                fases de {dealStatus === 'won' ? 'perda' : 'ganho'}, reabra o negócio.
              </p>
            )}
          </div>

          {/* Observações */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Observações (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
              maxLength={MAX_NOTES}
              rows={3}
              placeholder="Digite observações sobre a alteração da fase..."
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Alterar Fase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
